/* eslint-disable jsdoc/require-returns, jsdoc/require-param-description, jsdoc/require-param-type */
import { titleCase } from 'title-case';

/* Helpers */

const SIZE_EXPANSIONS = {
  'S/M': 'Small/Medium',
  XS: 'Extra Small',
  S: 'Small',
  M: 'Medium',
  L: 'Large',
  XL: 'Extra Large',
  XXL: 'Extra Extra Large',
};

const expandText = (text = '') => SIZE_EXPANSIONS[text] ?? titleCase(String(text ?? ''));

/**
 * Special characters and their equivalents for easy string comparison. Characters are looked up
 * via their property name which does not work with these special characters unless they are
 * quoted.
 */
/* eslint-disable quote-props */
const symbolsToReplace = {
  'ē': 'e',
  'í': 'i',
  'ü': 'u',
  'ū': 'u',
};
  /* eslint-enable quote-props */

const replaceSpecialCharacters = (str = '') => {
  let result = str;

  Object.keys(symbolsToReplace).forEach((symbol) => {
    result = result.replaceAll(symbol, symbolsToReplace[symbol]);
  });

  return result;
};

const slugify = (value = '') => {
  const cleanedString = replaceSpecialCharacters(String(value ?? ''))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return cleanedString;
};

const extractPid = (url = '') => {
  const match = String(url ?? '').match(/\/p\/(\d+)\//);
  return match ? Number(match[1]) : null;
};

const findSelectedImageSku = (groups = []) => {
  const imageGroup = (groups ?? []).find((group) => group?.type === 'image');
  const selected = (imageGroup?.options ?? []).find((option) => option?.selected);
  return selected?.sku ?? null;
};

// Group titles matching this regex keep their raw `text` value — no
// title-case / size expansion. Mirrors the store's getAllOptionItems getter.
const OPTION_TEXT_EXCLUDE_REGEX = /(model|version|size|case)/i;

const convertEmsOption = (option = {}, selectedImageSku = null, skipExpandText = false) => {
  const rawText = option?.text ?? '';
  const displayText = rawText && !skipExpandText ? expandText(rawText) : rawText;
  const pid = extractPid(option?.url);
  const isSelected = option?.selected === true;
  return {
    id: slugify(option?.id ?? ''),
    altText: displayText,
    text: displayText,
    order: option?.order ?? 0,
    ...(pid !== null ? { pid } : {}),
    sku: isSelected && selectedImageSku ? selectedImageSku : (option?.sku ?? ''),
  };
};

const convertImageOption = (option = {}) => {
  const pid = extractPid(option?.url);
  return {
    id: option?.image ?? '',
    altText: option?.altText ?? '',
    text: option?.text ?? '',
    order: option?.order ?? 0,
    ...(pid !== null ? { pid } : {}),
    sku: option?.sku ?? '',
    image: option?.image ?? '',
  };
};

const convertGroup = (group = {}, allGroups = []) => {
  const type = group?.type ?? '';
  const isEms = type === 'ems';
  const isImage = type === 'image';
  const selectedImageSku = findSelectedImageSku(allGroups);
  const skipExpandText = OPTION_TEXT_EXCLUDE_REGEX.test(group?.title ?? '');
  const options = (group?.options ?? []).map((option) => (
    isEms
      ? convertEmsOption(option, selectedImageSku, skipExpandText)
      : convertImageOption(option)
  ));
  // For the enableFilterImage group, mirror the sku into the text field.
  const normalizedOptions = (group?.id ?? '') === 'enableFilterImage'
    ? options.map((option) => ({ ...option, text: option?.sku ?? '' }))
    : options;
  // Sort the options in ascending order by SKU for stable rendering and comparison.
  const sortedOptions = [...normalizedOptions].sort((a, b) => (a?.sku ?? '').localeCompare(b?.sku ?? ''));
  return {
    id: group?.id ?? '',
    order: group?.order ?? 0,
    ...(isEms ? { title: group?.title ?? '' } : {}),
    ...(isEms && group?.tooltip ? { tooltip: group.tooltip } : {}),
    type,
    options: sortedOptions,
    ...(isImage ? { title: 'Model/Color' } : {}),
  };
};

/**
 * Convert a bootstrap productEmsList to the new emsDetails shape.
 * - Group keys reordered; image groups drop title/tooltip.
 * - EMS options: `altText` expanded from `text`, `id` slugified from altText,
 *   `pid` parsed from URL, and `sku` set to the currently-selected image
 *   group's sku when the option is the selected one.
 * - Image options: `id`/`image` from URL, `text` mirrors `altText`, `pid`
 *   parsed from URL.
 * @param list
 */
export const convertBootstrapProductEmsList = (list = []) => (
  (Array.isArray(list) ? list : []).map((group) => convertGroup(group, list))
);
